import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import type { HierarchyInputProps } from './HierarchyInput.types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';

type ParentKey = string | undefined;

const DEFAULT_PAGE_SIZE = 50;

interface HierarchyOptionItemProps {
  itemId: string;
  itemLabel: string;
  queryKey: string;
  isSelected: boolean;
  checkHasChildren: () => Promise<boolean>;
  onSelectItem: () => void;
  onDeselectItem: () => void;
  onGoToChildren: () => void;
}

function HierarchyOptionItem({
  itemId,
  itemLabel,
  queryKey,
  isSelected,
  checkHasChildren,
  onSelectItem,
  onDeselectItem,
  onGoToChildren,
}: HierarchyOptionItemProps) {
  const { data: hasChildren = false } = useQuery({
    queryKey: [queryKey, 'children-check', itemId],
    queryFn: checkHasChildren,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        if (isSelected) {
          onDeselectItem();
        } else {
          onSelectItem();
        }
      }}
      className={cn(
        'h-12 cursor-pointer px-3 py-2.5 text-sm text-primary',
        'flex items-center justify-between',
        isSelected && 'bg-prominent font-medium',
      )}
    >
      <span className="flex-1 text-left">{itemLabel}</span>
      {hasChildren && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onGoToChildren();
          }}
          className="ml-2 rounded p-1 text-primary opacity-50 hover:bg-prominent hover:opacity-100"
          aria-label={`Ver subniveles de ${itemLabel}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </DropdownMenuItem>
  );
}

export function HierarchyInput<T extends { id: string }>({
  value,
  onSelect,
  fetchItems,
  fetchItemById,
  getLabel,
  getParent,
  queryKey,
  placeholder = 'Seleccionar',
  label,
  helperText,
  error,
  disabled = false,
  className,
}: HierarchyInputProps<T>) {
  const [open, setOpen] = useState(false);
  const [currentParentId, setCurrentParentId] = useState<ParentKey>(undefined);
  const [parentHistory, setParentHistory] = useState<ParentKey[]>([]);
  const [parentNameHistory, setParentNameHistory] = useState<string[]>([]);
  const [selectedDisplay, setSelectedDisplay] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>();

  const effectiveSelectedId = value ?? '';

  const {
    data: itemsData,
    refetch: refetchItems,
    isFetching: isFetchingItems,
  } = useQuery({
    queryKey: [queryKey, 'items', currentParentId ?? 'root', pageSize],
    queryFn: () => fetchItems({ parentId: currentParentId, page: 1, pageSize }),
  });

  const { data: selectedPath } = useQuery<string>({
    queryKey: [queryKey, 'path', effectiveSelectedId],
    enabled: !!effectiveSelectedId,
    queryFn: async () => {
      const names: string[] = [];
      const visited = new Set<string>();
      let currentId: string | undefined = effectiveSelectedId;
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const item = await fetchItemById(currentId);
        names.unshift(getLabel(item));
        currentId = getParent(item) ?? undefined;
      }
      return names.join(' / ');
    },
    staleTime: 5 * 60 * 1000,
  });

  const currentOptions = useMemo(() => itemsData?.items ?? [], [itemsData]);
  const hasMore = itemsData?.hasMore ?? false;
  const handleLoadMore = () => setPageSize((prev) => prev + DEFAULT_PAGE_SIZE);

  const displayValue = useMemo(() => {
    if (!effectiveSelectedId) return '';
    if (selectedDisplay?.id === effectiveSelectedId) return selectedDisplay.label;
    if (selectedPath) return selectedPath;
    return '';
  }, [effectiveSelectedId, selectedDisplay, selectedPath]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setContentWidth(triggerRef.current?.offsetWidth);
      void refetchItems();
    }
  };

  const handleGoToChildren = (itemId: string, itemName: string) => {
    setParentHistory((prev) => [...prev, currentParentId]);
    setParentNameHistory((prev) => [...prev, itemName]);
    setCurrentParentId(itemId);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const handleBack = () => {
    setParentHistory((prev) => {
      if (prev.length === 0) {
        setCurrentParentId(undefined);
        return prev;
      }
      const newHistory = [...prev];
      const previousParent = newHistory.pop();
      setCurrentParentId(previousParent);
      setParentNameHistory((prevNames) => {
        const updated = [...prevNames];
        updated.pop();
        return updated;
      });
      return newHistory;
    });
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <div ref={triggerRef} className="w-full">
          <Input
            type="text"
            value={displayValue}
            readOnly
            placeholder={placeholder}
            label={label}
            helperText={helperText}
            error={error}
            disabled={disabled}
            className={cn('w-full cursor-pointer', className)}
            iconEnd={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className={cn(
                  'opacity-50 transition-transform duration-200',
                  open ? 'rotate-180' : 'rotate-0',
                )}
              >
                <path d="M7 9.5L12 14.5L17 9.5H7Z" fill="#868686" />
              </svg>
            }
          />
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="z-50 rounded-lg border border-outline bg-select-background shadow-lg p-0 overflow-y-auto"
        style={{ width: contentWidth ? `${contentWidth}px` : 'auto' }}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {parentHistory.length > 0 && (
          <button
            type="button"
            onClick={handleBack}
            className="flex w-full items-center gap-2 border-b border-outline px-3 py-2 text-left text-sm text-primary hover:bg-prominent"
          >
            <ChevronLeft className="h-4 w-4" />
            Atrás
          </button>
        )}

        {currentOptions.length === 0 && (
          <div className="px-3 py-2 text-sm text-primary opacity-50">Sin opciones</div>
        )}

        {currentOptions.map((item) => {
          const itemLabel = getLabel(item);
          return (
            <HierarchyOptionItem
              key={item.id}
              itemId={item.id}
              itemLabel={itemLabel}
              queryKey={queryKey}
              isSelected={effectiveSelectedId === item.id}
              checkHasChildren={() =>
                fetchItems({ parentId: item.id, page: 1, pageSize: 1 }).then(
                  (r) => r.items.length > 0,
                )
              }
              onSelectItem={() => {
                const fullPath = [...parentNameHistory, itemLabel].join(' / ');
                setSelectedDisplay({ id: item.id, label: fullPath });
                onSelect(item.id);
                setOpen(false);
              }}
              onDeselectItem={() => {
                setSelectedDisplay(null);
                onSelect(null);
                setOpen(false);
              }}
              onGoToChildren={() => handleGoToChildren(item.id, itemLabel)}
            />
          );
        })}

        {hasMore && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleLoadMore();
            }}
            disabled={isFetchingItems}
            className="w-full border-t border-outline px-3 py-2 text-center text-sm text-primary opacity-70 hover:bg-prominent hover:opacity-100 disabled:cursor-not-allowed"
          >
            {isFetchingItems ? 'Cargando...' : 'Ver más'}
          </button>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
